import { GetStaticProps, NextPage } from 'next';
import Head from 'next/head';
import path from 'path';
import fs from 'fs';

import { generateFullPageMetadata, PageHeader, Body, SocialShareButtons, Related, FeaturedContent } from './../components';
import type { RelatedItem } from './../components';
import { getPagePaths, resolveThumbnailInfoJson } from './../functions';
import { build_content } from './../utils/content_parser';
import { derivePageDates } from './../utils/page_dates';
import { getAllPageDefinitions } from './../utils/contentWalker';

import type { ImageData, PageData, SourceProps } from '@interfaces';

const HomePage: NextPage<SourceProps & { allPages?: RelatedItem[]; featPages?: any[] }> = ({ pageData, imageData, allPages, featPages = [] }) => {
  const metadata = generateFullPageMetadata(pageData, imageData);

  return (
    <>
      <Head>
        <title>{metadata.pageTitle}</title>
        <link rel="canonical" href={metadata.canonicalUrl} />

        {Object.entries(metadata.meta).map(([name, content]) => (
          <meta key={name} name={name} content={content} />
        ))}

        {Object.entries(metadata.openGraph).map(([property, value]) => {
          if (Array.isArray(value)) {
            return value.map((v, i) => (
              <meta key={`${property}-${i}`} property={property} content={v} />
            ));
          }
          return <meta key={property} property={property} content={value} />;
        })}

        {Object.entries(metadata.twitter).map(([name, content]) => (
          <meta key={name} name={name} content={content} />
        ))}

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(metadata.schemaGraph),
          }}
        />
      </Head>

      <SocialShareButtons
        text={pageData.description}
        url={pageData.share_url}
        via="thedailydialectics"
        hashtags={pageData.keywords_str}
        title={pageData.title}
        description={pageData.description}
        summary={pageData.summary}
        image={metadata.primaryImage}
      />

      <PageHeader meta={pageData} baseUrl={pageData.BASE_URL} />
      <FeaturedContent pages={featPages} heading="Discover something from across the site" />
      <Body meta={pageData} pdfViewer={pageData?.pdfViewer} />
      <Related items={pageData.related} heading="Related content" />
      <Related items={allPages} heading="Explore all topics" />
    </>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  try {
    const dirname = 'home';
    const filename = 'home';

    const { jsonPath, contentsPath } = getPagePaths(dirname, filename);

    const jsonContents = fs.readFileSync(jsonPath, 'utf8');
    const pageData: PageData = JSON.parse(jsonContents);

    // Real dates for the home page (explicit variables.json field when present,
    // else the content files' mtimes) — see utils/page_dates.
    const dates = derivePageDates(jsonPath, contentsPath);
    if (dates) {
      pageData.datePublished = dates.datePublished;
      pageData.dateModified = dates.dateModified;
    }

    // Resolve the thumbnail's info.json the same way the dynamic route does,
    // so the home hero/metadata don't break when the thumbnail lives under
    // PUBLIC_ROOT rather than relative to the process cwd.
    // A missing/legacy thumbnail must not 404 the home page — degrade to no hero.
    const pageDir = path.dirname(contentsPath);
    let imageData: ImageData = {} as ImageData;
    try {
      const thumbnailPath = resolveThumbnailInfoJson(pageData.thumbnail, pageDir);
      imageData = JSON.parse(fs.readFileSync(thumbnailPath, 'utf8'));
      pageData.imageData = imageData;
    } catch (e) {
      console.warn(`home thumbnail unresolved: ${(e as Error).message}`);
    }

    // Related links for the home page itself, plus a global index of every
    // page so the home can link out to all content (both from generate_related).
    const relatedPath = path.join(pageDir, 'related.json');
    if (fs.existsSync(relatedPath)) {
      pageData.related = JSON.parse(fs.readFileSync(relatedPath, 'utf8'));
    }

    let allPages: any[] = [];
    try {
      const pagesRoot = path.dirname(path.dirname(path.dirname(jsonPath)));
      const indexPath = path.join(pagesRoot, 'related_index.json');
      if (fs.existsSync(indexPath)) {
        const idx = JSON.parse(fs.readFileSync(indexPath, 'utf8')) as Record<string, any>;
        allPages = Object.entries(idx)
          .filter(([key]) => key !== 'home/home')
          .map(([key, v]) => {
            const [section, slug] = key.split('/');
            return { section, slug, title: v.title || slug, href: v.href || '', image: v.image || '' };
          })
          .sort((a, b) => a.section.localeCompare(b.section) || a.title.localeCompare(b.title));
      }
    } catch (e) {
      console.error('related_index load error:', e);
    }

    const pageContents = fs.readFileSync(contentsPath, 'utf8');
    pageData.content = await build_content(pageContents, pageData, filename);

    // Featured pool: a random MIX across the site — content pages (embedded as an
    // iframe of the page via ?embed=1), standalone videos (native <video> player),
    // and standalone PDFs (the site's /pdfs gallery viewer). A picked page that is
    // itself primarily a PDF/video uses the standardized viewer/player too.
    // See components/MediaCarousel/FeaturedContent.
    const BASE_FC = process.env.NEXT_PUBLIC_API_BASE?.replace(/\/+$/, '') || 'https://thedailydialectics.com';
    const shuffle = <T,>(a: T[]): T[] => {
      for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; }
      return a;
    };
    // URL-encode each path segment (hub media paths contain spaces etc.).
    const encPath = (p: string) => p.split('/').map((s) => (s ? encodeURIComponent(s) : s)).join('/');
    const isVideoMedia = (m: any) =>
      !!m && (['youtube', 'vimeo', 'video'].includes(m.kind) ||
        (typeof m.embedUrl === 'string' &&
          /youtube\.com\/embed|player\.vimeo\.com|\.(mp4|webm)(\?|$)/i.test(m.embedUrl)));

    // --- content pages (kind depends on the page's own primary content) ---
    const pageItems: any[] = [];
    const defs = shuffle(getAllPageDefinitions().filter((d) => d.section !== 'home'));
    for (const { section, slug } of defs) {
      if (pageItems.length >= 6) break;
      try {
        const { jsonPath, contentsPath } = getPagePaths(section, slug);
        if (!fs.existsSync(contentsPath)) continue;
        const vars: any = JSON.parse(fs.readFileSync(jsonPath, 'utf8'));
        const raw = fs.readFileSync(contentsPath, 'utf8');
        if (!raw.trim()) continue; // nothing to show

        const title = vars.title || slug;
        const pagePath = `/${section}/${slug}`;
        const url = `${BASE_FC}${pagePath}`;

        const pdfPicker = vars.pdfPicker?.items?.length ? vars.pdfPicker : undefined;
        const pdfs = Array.isArray(vars.pdfs) && vars.pdfs.length ? vars.pdfs : undefined;
        const pdfViewer =
          vars.pdfViewer && (typeof vars.pdfViewer === 'object' || vars.pdfViewer === true)
            ? vars.pdfViewer
            : undefined;
        if (pdfPicker || pdfs || pdfViewer) {
          pageItems.push({ kind: 'pdf', title, url, section, pdf: { pdfPicker, pdfs, pdfViewer } });
          continue;
        }

        const media: any[] = Array.isArray(vars.media) ? vars.media : [];
        const video = media.find(isVideoMedia);
        if (video?.embedUrl && raw.trim().length < 400) {
          pageItems.push({ kind: 'video', title, url, section, embedUrl: video.embedUrl });
          continue;
        }

        pageItems.push({ kind: 'page', title, url, section, embedUrl: `${pagePath}?embed=1` });
      } catch { /* skip unreadable page */ }
    }

    // --- standalone videos + PDFs from the browse hubs (random sample) ---
    const readGen = (name: string): any[] => {
      try {
        const j = JSON.parse(fs.readFileSync(path.join(process.cwd(), 'src', 'generated', name), 'utf8'));
        return Array.isArray(j) ? j : (j.items || []);
      } catch { return []; }
    };
    const videoItems = shuffle(readGen('videos-browse.json'))
      .filter((v: any) => v && v.m && v.t)
      .slice(0, 4)
      .map((v: any) => ({
        kind: 'video', title: v.t, section: 'videos',
        url: `${BASE_FC}${encPath(v.u || v.m)}`,
        mediaUrl: encPath(v.m),
        poster: v.th ? encPath(v.th) : undefined,
      }));
    const pdfItems = shuffle(readGen('pdfs-browse.json'))
      .filter((p: any) => p && p.u && p.t)
      .slice(0, 4)
      .map((p: any) => ({
        kind: 'pdf', title: p.t, section: p.s || 'pdfs',
        url: `${BASE_FC}${encPath(p.u)}`,
        embedUrl: encPath(p.u),
      }));

    const featPages: any[] = shuffle([...pageItems, ...videoItems, ...pdfItems]);

    return {
      props: {
        pageData,
        imageData,
        allPages,
        featPages,
      },
    };
  } catch (error) {
    console.error('Homepage build error:', error);
    return { notFound: true };
  }
};

export default HomePage;
